import React, { useMemo } from "react";
import { Alert, Space, Tag, Tour, Typography, theme } from "antd";
import { appThemeColors } from "../../../theme/AppTheme";

const target = name => () => document.querySelector(`[data-payment-tour="${name}"]`);

function MoneyFlowDemo() {
  return <div className="pv2-tour-demo" aria-label="Payment balance example">
    <div><span>Invoice</span><strong>AED 2,060</strong></div><b>→</b>
    <div><span>Received</span><strong>AED 800</strong></div><b>→</b>
    <div><span>Due</span><strong>AED 1,260</strong></div>
  </div>;
}

export function PaymentsV2Tour({ open, onClose, locked }) {
  const { token } = theme.useToken();
  const steps = useMemo(() => [
    {
      title: "One payment workspace",
      description: "The invoice, COD / Pay Later records, payments, corrections, and refunds all live here. Do not use legacy payment screens for this query.",
      target: target("workspace"), placement: "center",
    },
    {
      title: locked ? "The invoice is locked" : "Preview, then lock the invoice",
      description: <div className="pv2-tour-copy"><Typography.Paragraph>The server builds the invoice from the latest customer-shared quote. The browser never calculates or submits commercial totals.</Typography.Paragraph><Alert type="info" showIcon message={locked ? "The locked revision is the immutable commercial snapshot." : "Lock only after the customer confirms the quote."} /></div>,
      target: target("invoice"), placement: "bottom",
    },
    {
      title: "Create a payment or manage an existing one",
      description: <div className="pv2-tour-copy"><Typography.Paragraph>Use Send payment link, Record payment or Pay later / COD. Find corrections, cancellations and refunds in each payment ledger row's menu.</Typography.Paragraph><Space wrap size={[4, 6]}><Tag color="green">Confirmed payment = money received</Tag><Tag color="blue">Online link = payment request</Tag><Tag color="gold">COD / Pay Later ≠ received money</Tag></Space></div>,
      target: target("actions"), placement: "bottom",
    },
    {
      title: "Online payment links do not settle an invoice",
      description: <div className="pv2-tour-copy"><Typography.Paragraph>The server calculates the processing fee. Preparing or creating a live link reserves an amount; only provider confirmation records received money.</Typography.Paragraph><Tag color="blue">Payment request ≠ received money</Tag></div>,
      target: target("actions"), placement: "bottom",
    },
    {
      title: "Understand the balance",
      description: <div className="pv2-tour-copy"><MoneyFlowDemo /><Typography.Paragraph>Pending verification and active link reservations appear separately. Neither is cleared money.</Typography.Paragraph></div>,
      target: target("summary"), placement: "bottom",
    },
    {
      title: "Received payment and order approval are different",
      description: "An approved COD / Pay Later record may allow the order to proceed while money remains due. An unresolved problem may still block the order after money is received.",
      target: target("clearance"), placement: "bottom",
    },
    {
      title: "The activity ledger explains every change",
      description: <div className="pv2-tour-copy"><Typography.Paragraph>Use this history to distinguish online payment links, confirmed payments, COD / Pay Later, cheques, corrections, customer refund requests, and refunds actually paid.</Typography.Paragraph><Alert type="warning" showIcon message="A reversal corrects an invalid payment record. A refund records money owed back." /></div>,
      target: target("activity"), placement: "top",
    },
  ], [locked]);
  // Tour cards portal to the body; give them the opener's theme colours.
  const themedSteps = useMemo(() => steps.map(step => ({ ...step, style: appThemeColors(token) })), [steps, token]);

  return <Tour open={open} onClose={onClose} rootClassName="pv2-tour" steps={themedSteps} arrow mask={{ color: "rgba(5, 12, 24, 0.58)" }} scrollIntoViewOptions={{ behavior: "smooth", block: "center" }} type="primary" />;
}
